import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Shield, Search, Filter } from 'lucide-react';
import { Input } from '../components/ui/input';
import { format, parseISO } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-amber-100 text-amber-700',
  delete: 'bg-red-100 text-red-700',
};

const ENTITY_LABELS = {
  account: '🏥 Account',
  sales_call: '📞 Call',
  field_call: '🤝 Field Call',
  telephonic_call: '📱 Telephonic Call',
  lead: '📋 Lead',
  contact: '👤 Contact',
};

const ActionLog = ({ onLogout }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/audit-log?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(r.data);
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Admin access required' : 'Failed to load logs');
    } finally { setLoading(false); }
  };

  const filtered = logs.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q || l.entity_name?.toLowerCase().includes(q) ||
      l.performed_by?.toLowerCase().includes(q) ||
      l.performed_by_name?.toLowerCase().includes(q) ||
      l.action_type?.toLowerCase().includes(q);
    const matchType = filterType === 'all' || l.action_type === filterType;
    return matchSearch && matchType;
  });

  const formatDate = (ts) => {
    try { return format(parseISO(ts), 'dd MMM yy, hh:mm a'); } catch { return ts || '—'; }
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-100 rounded-xl">
              <Shield className="h-6 w-6 text-violet-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Action Log
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">All modifications and deletions — admin only</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all','create','update','delete'].map(t => (
              <button key={t} onClick={() => setFilterType(t)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all capitalize ${
                  filterType === t
                    ? t === 'delete' ? 'bg-red-600 text-white border-red-600'
                    : t === 'update' ? 'bg-amber-500 text-white border-amber-500'
                    : t === 'create' ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-teal-700 text-white border-teal-700'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
                }`}>{t === 'all' ? 'All Actions' : t}</button>
            ))}
          </div>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, action..."
            className="pl-10 h-11 rounded-xl" />
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full mx-auto" />
            <p className="text-slate-500 mt-4">Loading audit log...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No actions logged yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">When</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Action</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Record</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">By</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Changes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map(log => (
                    <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-4 whitespace-nowrap text-xs text-slate-500">{formatDate(log.timestamp)}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded-lg font-semibold capitalize ${ACTION_COLORS[log.action_type] || 'bg-slate-100 text-slate-600'}`}>
                          {log.action_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-600">
                        {ENTITY_LABELS[log.entity_type] || log.entity_type}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-800 text-sm">{log.entity_name}</span>
                        {log.note && <p className="text-xs text-slate-400 mt-0.5">{log.note}</p>}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-xs font-medium text-slate-700">{log.performed_by_name || log.performed_by}</p>
                        <p className="text-xs text-slate-400">{log.performed_by}</p>
                      </td>
                      <td className="py-3 px-4 max-w-xs">
                        {Object.keys(log.after || {}).length > 0 && (
                          <div className="space-y-0.5">
                            {Object.entries(log.after).slice(0, 3).map(([field, val]) => (
                              <div key={field} className="text-xs">
                                <span className="font-medium text-slate-600">{field.replace(/_/g, ' ')}: </span>
                                <span className="line-through text-red-400">{String(log.before?.[field] || '—').slice(0, 20)}</span>
                                <span className="text-slate-400"> → </span>
                                <span className="text-emerald-600">{String(val || '—').slice(0, 20)}</span>
                              </div>
                            ))}
                            {Object.keys(log.after).length > 3 && (
                              <p className="text-xs text-slate-400">+{Object.keys(log.after).length - 3} more fields</p>
                            )}
                          </div>
                        )}
                        {log.action_type === 'delete' && (
                          <span className="text-xs text-red-500 font-medium">Record permanently deleted</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-400">
              Showing {filtered.length} of {logs.length} entries
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ActionLog;
